import {URI} from '../rest/api';
import locals from '../lib/localStorage';
import type {SessionReplayChunk, SessionReplayDetail, SessionReplayListResponse} from './types';
import {getCachedChunk, putCachedChunk} from './chunkCache';

export function getAuthHeader(): string {
	return 'Bearer ' + localStorage.getItem(locals.AUTH_ID_TOKEN);
}

/**
 * List session replay VISITS with pagination and optional filters (OWA-664).
 * One entry per user sitting; the per-tab sessions live inside each visit.
 */
export async function listVisits(params: {
	limit?: number;
	offset?: number;
	userId?: string;
	status?: string;
	startDate?: string;
	endDate?: string;
}): Promise<SessionReplayListResponse> {
	const query = new URLSearchParams();
	if (params.limit != null) query.set('limit', String(params.limit));
	if (params.offset != null) query.set('offset', String(params.offset));
	if (params.userId) query.set('userId', params.userId);
	if (params.status) query.set('status', params.status);
	if (params.startDate) query.set('startDate', params.startDate);
	if (params.endDate) query.set('endDate', params.endDate);

	const response = await fetch(`${URI}/replay/sessions?${query}`, {
		headers: {'Authentication': getAuthHeader()},
	});

	if (!response.ok) {
		throw new Error(`Failed to list sessions: ${response.status}`);
	}

	return response.json();
}

/**
 * Get session detail including chunk list.
 */
export async function getSessionDetail(sessionId: string): Promise<SessionReplayDetail> {
	const response = await fetch(`${URI}/replay/sessions/${sessionId}`, {
		headers: {'Authentication': getAuthHeader()},
	});

	if (!response.ok) {
		throw new Error(`Failed to get session: ${response.status}`);
	}

	return response.json();
}

/**
 * Download the gzipped chunk data directly (proxied through the server).
 * Low-level fallback — most callers should use `fetchChunkBytes` instead so
 * they get the cache and direct-S3 fast paths.
 */
export async function getChunkData(sessionId: string, chunkIndex: number): Promise<Uint8Array> {
	const response = await fetch(
		`${URI}/replay/sessions/${sessionId}/chunks/${chunkIndex}`,
		{headers: {'Authentication': getAuthHeader()}},
	);

	if (!response.ok) {
		throw new Error(`Failed to download chunk: ${response.status}`);
	}

	return new Uint8Array(await response.arrayBuffer());
}

/**
 * Fetch a chunk's gzipped bytes using (in order):
 *   1. IndexedDB cache, if the chunk is present and unexpired.
 *   2. The presigned S3 URL embedded in the chunk metadata, for a direct
 *      browser→S3 download that skips the API server entirely.
 *   3. The authenticated proxy endpoint, as a fallback for expired URLs,
 *      missing CORS, or older server builds that don't emit downloadUrl.
 *
 * Successful downloads are written back to the cache so subsequent reads
 * within the URL's lifetime skip the network altogether.
 */
export async function fetchChunkBytes(chunk: SessionReplayChunk): Promise<Uint8Array> {
	const cached = await getCachedChunk(chunk.sessionId, chunk.chunkIndex);
	if (cached) return cached;

	// Try direct S3 via presigned URL, if still valid.
	if (chunk.downloadUrl && chunk.urlExpiresAt && chunk.urlExpiresAt > Date.now()) {
		try {
			const response = await fetch(chunk.downloadUrl);
			if (response.ok) {
				const bytes = new Uint8Array(await response.arrayBuffer());
				await putCachedChunk(chunk.sessionId, chunk.chunkIndex, bytes, chunk.urlExpiresAt);
				return bytes;
			}
			console.warn(`Session replay: direct S3 download for chunk ${chunk.chunkIndex} returned ${response.status}, falling back to proxy`);
		}
		catch (err) {
			console.warn(`Session replay: direct S3 download for chunk ${chunk.chunkIndex} failed, falling back to proxy`, err);
		}
	}

	// Proxy fallback.
	const bytes = await getChunkData(chunk.sessionId, chunk.chunkIndex);
	const cacheExpiry = chunk.urlExpiresAt ?? (Date.now() + 6 * 60 * 60 * 1000);
	await putCachedChunk(chunk.sessionId, chunk.chunkIndex, bytes, cacheExpiry);
	return bytes;
}

/**
 * Toggle the favorited flag on a VISIT (OWA-664). `visitId` is the visit key
 * from the list; the server flips every tab in the visit together and returns
 * the new state.
 */
export async function toggleFavorite(visitId: string): Promise<{visitId: string; favorited: boolean}> {
	const response = await fetch(`${URI}/replay/sessions/${visitId}/favorite`, {
		method: 'POST',
		headers: {'Authentication': getAuthHeader()},
	});

	if (!response.ok) {
		throw new Error(`Failed to toggle favorite: ${response.status}`);
	}

	return response.json();
}

/**
 * Delete (soft-delete) a whole VISIT (OWA-664) — every tab in it. `visitId` is
 * the visit key from the list.
 */
export async function deleteVisit(visitId: string): Promise<void> {
	const response = await fetch(`${URI}/replay/sessions/${visitId}`, {
		method: 'DELETE',
		headers: {'Authentication': getAuthHeader()},
	});

	if (!response.ok) {
		throw new Error(`Failed to delete session: ${response.status}`);
	}
}
